import { useState, useMemo, useEffect } from 'react'
import { Plus, Target, CheckCircle, TrendingUp, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { PageHeader } from '@/components/layout'
import { StatCard } from '@/components/shared'
import { GoalCard, GoalDialog } from '@/components/goals'
import { useAppSelector, useAppDispatch } from '@/store/hooks'
import {
  addGoal,
  updateGoal,
  deleteGoal,
  addContribution,
  setGoalStatus,
} from '@/store/slices/goalsSlice'
import {
  recordActivity,
  updateAchievementProgress,
  unlockAchievement,
  visitPage,
} from '@/store/slices/gamificationSlice'
import type { Goal, GoalType, GoalStatus } from '@/types'
import { formatCurrency, formatPercentage } from '@/lib/utils'
import {
  checkGoalCreationAchievements,
  checkGoalCompletionAchievements,
  checkContributionAchievements,
  isGoalOnTrack,
} from '@/lib/achievements'
import { toast } from 'sonner'

export default function Goals() {
  const dispatch = useAppDispatch()
  const goals = useAppSelector(state => state.goals)
  const portfolio = useAppSelector(state => state.portfolio)
  const debt = useAppSelector(state => state.debt)
  const gamification = useAppSelector(state => state.gamification)
  
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingGoal, setEditingGoal] = useState<Goal | null>(null)
  const [activeTab, setActiveTab] = useState<'active' | 'completed' | 'all'>('active')

  // Track page visit for explorer achievement
  useEffect(() => {
    dispatch(visitPage('goals'))
  }, [dispatch])

  // Filter goals by status
  const filteredGoals = useMemo(() => {
    const sorted = [...goals.goals].sort((a, b) => a.priority - b.priority)
    
    switch (activeTab) {
      case 'active':
        return sorted.filter(g => g.status === 'active')
      case 'completed':
        return sorted.filter(g => g.status === 'completed')
      default:
        return sorted
    }
  }, [goals.goals, activeTab])

  // Calculate stats
  const stats = useMemo(() => {
    const activeGoals = goals.goals.filter(g => g.status === 'active')
    const completedGoals = goals.goals.filter(g => g.status === 'completed')
    const onTrackCount = activeGoals.filter(g => isGoalOnTrack(g) !== false).length
    
    return {
      active: activeGoals.length,
      completed: completedGoals.length,
      totalSaved: goals.totalSaved,
      onTrackPercentage: activeGoals.length > 0 
        ? (onTrackCount / activeGoals.length) * 100 
        : 100,
    }
  }, [goals])

  // Check and unlock achievements
  const processAchievements = (results: { achievementId: string; shouldUnlock: boolean; newProgress: number }[]) => {
    results.forEach(({ achievementId, shouldUnlock, newProgress }) => {
      const achievement = gamification.achievements[achievementId as keyof typeof gamification.achievements]
      if (achievement && !achievement.unlockedAt) {
        if (shouldUnlock) {
          dispatch(unlockAchievement(achievementId as any))
          toast.success(`Achievement Unlocked: ${achievement.name}!`, {
            description: achievement.description,
          })
        } else {
          dispatch(updateAchievementProgress({ id: achievementId as any, progress: newProgress }))
        }
      }
    })
  }

  const handleCreateGoal = (data: {
    name: string
    type: GoalType
    targetAmount: number
    deadline: string | null
    color: string
    notes: string
    linkedAccountId: string | null
    linkedAccountType: 'investment' | 'debt' | null
  }) => {
    dispatch(addGoal(data))
    dispatch(recordActivity())
    
    // Create a temporary goal object to check achievements
    const newGoal: Goal = {
      id: 'temp',
      ...data,
      currentAmount: 0,
      createdAt: new Date().toISOString(),
      completedAt: null,
      status: 'active',
      priority: goals.goals.length + 1,
      contributions: [],
    }
    
    const achievementResults = checkGoalCreationAchievements(
      [...goals.goals, newGoal],
      newGoal
    )
    processAchievements(achievementResults)
    
    toast.success('Goal created!', {
      description: `"${data.name}" has been added to your goals.`,
    })
  }

  const handleUpdateGoal = (data: {
    name: string
    type: GoalType
    targetAmount: number
    deadline: string | null
    color: string
    notes: string
    linkedAccountId: string | null
    linkedAccountType: 'investment' | 'debt' | null
  }) => {
    if (editingGoal) {
      dispatch(updateGoal({
        id: editingGoal.id,
        updates: data,
      }))
      setEditingGoal(null)
      toast.success('Goal updated!')
    }
  }

  const handleDeleteGoal = (goalId: string) => {
    const goal = goals.goals.find(g => g.id === goalId)
    dispatch(deleteGoal(goalId))
    toast.success('Goal deleted', {
      description: goal ? `"${goal.name}" has been removed.` : undefined,
    })
  }

  const handleContribute = (goalId: string, amount: number, note: string) => {
    const goal = goals.goals.find(g => g.id === goalId)
    if (!goal) return

    const isFirst = goals.goals.every(g => g.contributions.length === 0)
    const newTotalSaved = goals.totalSaved + amount
    
    dispatch(addContribution({ goalId, amount, note }))
    dispatch(recordActivity())
    
    // Check contribution achievements
    const achievementResults = checkContributionAchievements(newTotalSaved, isFirst)
    processAchievements(achievementResults)
    
    // Check if this completed the goal
    const newAmount = goal.currentAmount + amount
    if (newAmount >= goal.targetAmount) {
      const completedGoal = { ...goal, currentAmount: newAmount, status: 'completed' as GoalStatus }
      const completionResults = checkGoalCompletionAchievements(
        goals.goals.map(g => g.id === goalId ? completedGoal : g),
        completedGoal
      )
      processAchievements(completionResults)
      
      toast.success('Goal Completed!', {
        description: `Congratulations! You've reached your "${goal.name}" goal!`,
      })
    } else {
      toast.success(`Added ${formatCurrency(amount)} to "${goal.name}"`)
    }
  }

  const handleStatusChange = (goalId: string, status: GoalStatus) => {
    const goal = goals.goals.find(g => g.id === goalId)
    dispatch(setGoalStatus({ goalId, status }))
    
    if (status === 'completed' && goal) {
      dispatch(recordActivity())
      const completedGoal = { ...goal, status }
      const achievementResults = checkGoalCompletionAchievements(
        goals.goals.map(g => g.id === goalId ? completedGoal : g),
        completedGoal
      )
      processAchievements(achievementResults)
      
      toast.success('Goal Completed!', {
        description: `Congratulations on completing "${goal.name}"!`,
      })
    }
  }

  const handleEditGoal = (goal: Goal) => {
    setEditingGoal(goal)
    setDialogOpen(true)
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Financial Goals"
        description="Track your progress towards financial milestones"
      >
        <Button onClick={() => {
          setEditingGoal(null)
          setDialogOpen(true)
        }}>
          <Plus className="mr-2 h-4 w-4" />
          Add Goal
        </Button>
      </PageHeader>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Active Goals"
          value={stats.active.toString()}
          icon={Target}
          changeType="neutral"
        />
        <StatCard
          title="Completed Goals"
          value={stats.completed.toString()}
          icon={CheckCircle}
          changeType="positive"
        />
        <StatCard
          title="Total Saved"
          value={formatCurrency(stats.totalSaved)}
          icon={TrendingUp}
          changeType="positive"
        />
        <StatCard
          title="On Track"
          value={formatPercentage(stats.onTrackPercentage)}
          icon={AlertCircle}
          changeType={stats.onTrackPercentage >= 80 ? 'positive' : stats.onTrackPercentage >= 50 ? 'neutral' : 'negative'}
        />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)}>
        <TabsList>
          <TabsTrigger value="active">
            Active ({goals.goals.filter(g => g.status === 'active').length})
          </TabsTrigger>
          <TabsTrigger value="completed">
            Completed ({goals.goals.filter(g => g.status === 'completed').length})
          </TabsTrigger>
          <TabsTrigger value="all">
            All ({goals.goals.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-6">
          {filteredGoals.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Target className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">
                {activeTab === 'active' && 'No active goals'}
                {activeTab === 'completed' && 'No completed goals yet'}
                {activeTab === 'all' && 'No goals yet'}
              </h3>
              <p className="text-muted-foreground mb-4">
                {activeTab === 'active' && 'Create your first financial goal to start tracking progress.'}
                {activeTab === 'completed' && 'Complete some goals to see them here!'}
                {activeTab === 'all' && 'Start by creating your first financial goal.'}
              </p>
              {(activeTab === 'active' || activeTab === 'all') && (
                <Button onClick={() => {
                  setEditingGoal(null)
                  setDialogOpen(true)
                }}>
                  <Plus className="mr-2 h-4 w-4" />
                  Create Goal
                </Button>
              )}
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filteredGoals.map((goal) => (
                <GoalCard
                  key={goal.id}
                  goal={goal}
                  onEdit={handleEditGoal}
                  onDelete={handleDeleteGoal}
                  onContribute={handleContribute}
                  onStatusChange={handleStatusChange}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Goal Dialog */}
      <GoalDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        goal={editingGoal}
        investments={portfolio.investments}
        debts={debt.debts}
        onSubmit={editingGoal ? handleUpdateGoal : handleCreateGoal}
      />
    </div>
  )
}
